import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store/store.ts";
import { IService, ISubService } from "../../type/type.ts";
import { fetchSubServices, addSubService, updateSubService, updateStatus, deleteSubService } from "../../slices/SubServiceMasterSlice.ts";
import Searchbar from "../../components/common/Searchbar.tsx";
import SideSheet from "../../components/common/Sidesheet.tsx";
import CustomTable from "../../components/common/Table.tsx";
import { Pagination, Switch } from "antd";
import * as yup from "yup";
import { Formik, Form, useFormik } from "formik";
import FormInput from "../../components/common/FormInput.tsx";
import { Image } from "antd";
import Confirmation from "../../components/common/Confirmation.tsx";
import { notification } from "antd";
import { capitalizeFirstLetter } from "../../utils/util.service.ts";
import { setOffset, setLimit, setSortField, setSortOrder } from "../../slices/SubServiceMasterSlice.ts";
import RequiredLabel from "../../components/common/RequiredLabel.tsx";
import { setBreadcrumbs } from "../../slices/BreadcrumbSlice.ts";
import { fetchServices } from "../../slices/ServiceMasterSlice.ts";

const SubServiceMaster = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { dataCount, offset, limit, subServices, loading } = useSelector((state: RootState) => {
    return state.subServiceMaster;
  });

  const [selectedSubService, setSelectedSubService] = useState<ISubService | null>(null);
  const [selectedService, setSelectedService] = useState<IService | null>(null);
  const [query, setQuery] = useState("");
  const [isSideSheetOpen, setSideSheetOpen] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmationMessage, setConfirmationMessage] = useState("Do you want to submit the form?");
  const [sortedData, setSortedData] = useState<ISubService[]>([]);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    order: "ascend" | "descend" | null;
  }>({
    key: "",
    order: null,
  });
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [subServiceToDelete, setSubServiceToDelete] = useState<ISubService | null>(null);

  useEffect(() => {
    dispatch(setBreadcrumbs([
      {
        title: "SubService Master",
        path: '/masters/subservice-master',

      }
    ]));

  }, [dispatch]);


  useEffect(() => {
    dispatch(fetchServices({
      searchInput: query,
      offset: offset,
      limit: limit,
      sortField: sortConfig.key ?? null,
      sortOrder: sortConfig.order && sortConfig.order === 'ascend' ? 1 : sortConfig.order === 'descend' ? -1 : null
    }));
  }, [dispatch, query, offset, limit, sortConfig]);

  const { services } = useSelector((state: RootState) => state.serviceMaster);


  useEffect(() => {
    dispatch(fetchSubServices({
      searchInput: query,
      offset: offset,
      limit: limit,
      sortField: sortConfig.key ?? null,
      sortOrder: sortConfig.order && sortConfig.order === 'ascend' ? 1 : sortConfig.order === 'descend' ? -1 : null
    })).then((response) => {
    });
  }, [dispatch, query, offset, limit, sortConfig]);

  const totalPages = Math.ceil(dataCount / limit);
  const showNotification = (type: "success" | "error", message: string) => {
    notification[type]({
      message,
      placement: "top",
    });
  };

  const handleEditClick = (subService: ISubService) => {
    setSelectedSubService(subService);
    const service = services.find(s => s.id === subService.service_id);
    setSelectedService(service || null);
    setSideSheetOpen(true);
  };

  const handleSearch = (value: string) => {
    setQuery(value);
    dispatch(setOffset(0));
  };

  const handlePaginationChange = (page: number, newPageSize?: number) => {

    dispatch(setOffset(page - 1));
    if (newPageSize && newPageSize !== limit) {
      dispatch(setLimit(newPageSize));
    }
  };

  const handleSortChange = (_: any, __: any, sorter: any) => {
    if (Array.isArray(sorter)) {
      sorter = sorter[0];
    }

    const { field, order } = sorter || {};

    const sortOrder = order === 'ascend' ? 1 : -1;

    setSortConfig({
      key: field || null,
      order: order || null
    });

    dispatch(setSortField(field));
    dispatch(setSortOrder(sortOrder));

    dispatch(setOffset(offset));
  };

  // const handleStatusChange = async (id: number, currentStatus: boolean) => {
  //   try {
  //     await dispatch(updateStatus({ id, isActive: !currentStatus })).unwrap();
  //     showNotification("success", "Status updated successfully");
  //     dispatch(fetchSubServices({
  //       searchInput: query,
  //       offset: offset,
  //       limit: limit,
  //       sortField: sortConfig.key ?? null,
  //       sortOrder: sortConfig.order && sortConfig.order === 'ascend' ? 1 : sortConfig.order === 'descend' ? -1 : null
  //     }));
  //   } catch (error) {
  //     showNotification("error", "Failed to update status");
  //   }
  // };

  const handleDeleteClick = (subService: ISubService) => {
    setSubServiceToDelete(subService);
    setConfirmationMessage("Are you sure you want to delete this subservice?");
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!subServiceToDelete) return;

    try {
      await dispatch(deleteSubService(subServiceToDelete.id)).unwrap();
      showNotification("success", "SubService deleted successfully");
      dispatch(fetchSubServices({
        searchInput: query,
        offset: offset,
        limit: limit,
        sortField: sortConfig.key ?? null,
        sortOrder: sortConfig.order && sortConfig.order === 'ascend' ? 1 : sortConfig.order === 'descend' ? -1 : null
      }));
    } catch (error) {
      showNotification("error", "Failed to delete subservice");
    } finally {
      setIsDeleteConfirmationOpen(false);
      setSubServiceToDelete(null);
    }
  };

  const columns = [
    {
      title: <span className="font-montserrat font-medium text-[13px] leading-5 tracking-normal text-[#6D6D6D] capitalize">S.No</span>,
      dataIndex: "serialNumber",
      key: "serialNumber",
      render: (_: any, __: any, index: number) =>
        (offset * limit + index + 1).toString().padStart(2, "0"),
      width: 50,
    },
    {
      title: <span className="font-montserrat font-medium text-[13px] leading-5 tracking-normal text-[#6D6D6D] capitalize">Service Name</span>,
      dataIndex: "service_name",
      key: "service_name",
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: sortConfig.key === "service_name" ? sortConfig.order : null,
      render: (service_name: string) => capitalizeFirstLetter(service_name),
    },
    {
      title: <span className="font-montserrat font-medium text-[13px] leading-5 tracking-normal text-[#6D6D6D] capitalize">SubService Name</span>,
      dataIndex: "subservice_name",
      key: "subservice_name",
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: sortConfig.key === "subservice_name" ? sortConfig.order : null,
      render: (subservice_name: string) => capitalizeFirstLetter(subservice_name),
    },
    {
      title: <span className="font-montserrat font-medium text-[13px] leading-5 tracking-normal text-[#6D6D6D] capitalize">Date of Creation</span>,
      dataIndex: "created_at",
      key: "created_at",
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: sortConfig.key === "created_at" ? sortConfig.order : null,
      render: (created_at: string) =>
        new Date(created_at).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
    },
    {
      title: <span className="text-[#6D6D6D]">Action</span>,
      dataIndex: 'action',
      key: 'action',
      sorter: false,
      render: (_: any, record: ISubService) => (
        <div className="flex items-center gap-3">
          <img
            src="/client/edit.svg"
            alt="Edit"
            className="w-5 h-5 cursor-pointer hover:scale-105 transition-transform"
            onClick={() => handleEditClick(record)}
          />
          <img
            src="/client/delete.svg"
            alt="Delete"
            className="w-5 h-5 cursor-pointer hover:scale-105 transition-transform"
            onClick={() => handleDeleteClick(record)}
          />
        </div>
      )
    }
  ];

  const validationSchema = yup.object({
    subServiceName: yup
      .string()
      .required("subservice name is required")
      .min(2, "subservice name must be at least 2 characters")
      .max(50, "subservice name must not exceed 50 characters")
      .matches(
        /^(?![0-9]+$)[a-zA-Z0-9 _-]+$/,
        "SubService name can only contain letters, numbers, spaces, underscores (_), hyphens (-), and cannot be empty."),
  });

  return (
    <>
      <Formik
        enableReinitialize
        key={selectedSubService?.id || "new"}
        initialValues={{
          subServiceName: selectedSubService?.subservice_name || "",
          service_id: selectedService?.name || "",
        }}
        validationSchema={validationSchema}
        validateOnChange={true}
        validateOnBlur={false}
        onSubmit={(values, { resetForm }) => {
          setConfirmationMessage(
            selectedSubService
              ? "Do you want to Update the form?"
              : "Do you want to submit the form?"
          );
          setIsConfirmationOpen(true);
          setConfirmAction(() => async () => {
            try {
              if (selectedSubService) {
                await dispatch(
                  updateSubService({
                    id: selectedSubService.id,
                    service_id: selectedService?.id || 0,
                    name: values.subServiceName,
                    subservice_name: values.subServiceName
                  })
                )
                  .unwrap()
                  .then(() => {
                    showNotification("success", "Service updated successfully.");
                  });
                setSelectedSubService(null);
              } else {
                await dispatch(
                  addSubService({
                    name: values.subServiceName,
                    subservice_name: values.subServiceName,
                    service_id: selectedService?.id || 0,
                  })
                )
                  .unwrap()
                  .then(() => {
                    showNotification("success", "SubService added successfully.");
                  });
              }
  
              dispatch(
                fetchSubServices({
                  searchInput: query,
                  offset: offset,
                  limit: limit,
                  sortField: sortConfig.key ?? null,
                  sortOrder:
                    sortConfig.order === "ascend"
                      ? 1
                      : sortConfig.order === "descend"
                      ? -1
                      : null,
                })
              );
  
              resetForm();
              setSideSheetOpen(false);
            } catch (error) {
              showNotification("error", "Operation failed.");
            }
            setIsConfirmationOpen(false);
          });
        }}
      >
        {({
          handleSubmit,
          resetForm,
          values,
          errors,
          touched,
          handleChange,
          setFieldTouched,
          validateField,
        }) => (
          <>
            <div className="bg-[#EDF1F6] h-[76px] mt-[10px] flex justify-between items-center px-[20px]">
              <p className="font-Montserrat font-semibold text-[20px] leading-[24.38px] text-[#0A0A0A]">
                SubService Master
              </p>
              <div className="flex justify-end gap-3">
                <Searchbar search={query} onSearch={handleSearch} />
                <SideSheet
                  btnLabel="Add Service"
                  width={443}
                  title={
                    selectedSubService ? "Edit SubService" : "Add SubService"
                  }
                  isOpen={isSideSheetOpen}
                  setOpen={setSideSheetOpen}
                  onSubmit={handleSubmit}
                  submitLabel={selectedSubService ? "Update" : "Submit"}
                  onCancel={() => {
                    setSideSheetOpen(false);
                    resetForm();
                    setSelectedSubService(null);
                  }}
                >
                  <Form>
                    <div className="mb-8">
                      <RequiredLabel>Service Name</RequiredLabel>
                      <FormInput
                        name="service_id"
                        type="select"
                        disabled={selectedSubService?.service_id}
                        options={
                          services?.map((service) => ({
                            value: service.name,
                            label: capitalizeFirstLetter(service.name),
                          })) ?? []
                        }
                        onChange={(selectedValue) => {
                          const selectedServiceObj = services?.find(
                            (s) => s.name === selectedValue
                          );
                          setSelectedService(selectedServiceObj || null);
                          handleChange({
                            target: {
                              name: "service_id",
                              value: selectedValue,
                            },
                          });
                        }}
                        value={values.service_id}
                        error={
                          touched.service_id && errors.service_id
                            ? errors.service_id
                            : ""
                        }
                      />
                    </div>
  
                    <div className="mb-8">
                      <RequiredLabel>SubService Name</RequiredLabel>
                      <FormInput
                        name="subServiceName"
                        type="text"
                        placeholder="Enter SubService name"
                        onChange={(value: string) => {
                          handleChange({
                            target: {
                              name: "subServiceName",
                              value: value,
                            },
                          });
                        }}
                        onBlur={() => {
                          setFieldTouched("subServiceName", true);
                          validateField("subServiceName");
                        }}
                        value={values.subServiceName}
                        error={
                          touched.subServiceName && errors.subServiceName
                            ? errors.subServiceName
                            : ""
                        }
                      />
                    </div>
                  </Form>
                </SideSheet>
              </div>
            </div>
  
            <div className="flex flex-col h-[85vh]">
              <div className="flex-1">
                <CustomTable<ISubService>
                  columns={columns}
                  dataSource={subServices}
                  loading={loading}
                  rowKey="id"
                  onChange={handleSortChange}
                />
              </div>
              <div className="sticky bottom-0 flex justify-end py-4">
                <Pagination
                  total={dataCount}
                  current={offset + 1}
                  pageSize={limit}
                  showSizeChanger={false}
                  onChange={handlePaginationChange}
                />
              </div>
            </div>
  
            <Confirmation
              label="Confirmation"
              message={confirmationMessage}
              isOpen={isConfirmationOpen || isDeleteConfirmationOpen}
              onClose={() => {
                setIsConfirmationOpen(false);
                setIsDeleteConfirmationOpen(false);
                setSubServiceToDelete(null);
              }}
              onConfirm={
                isDeleteConfirmationOpen
                  ? handleDeleteConfirm
                  : confirmAction || (() => {})
              }
              confirmButtonLabel="Confirm"
            />
          </>
        )}
      </Formik>
    </>
  );
  
};

export default SubServiceMaster;
